import { create } from 'zustand';
import api from '../api/axios';

export const useWeddingPlannerStore = create((set, get) => ({
  // State
  plans: [],
  currentPlan: null,
  categories: [],
  tasks: [],
  expenses: [],
  guests: [],
  guestGroups: [],
  guestStats: {},
  loading: false,
  error: null,

  // ═══════════════════════════════════════════════════════════════
  // PLANS
  // ═══════════════════════════════════════════════════════════════

  fetchPlans: async () => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.get('/wedding/plans');
      set({ plans: data.data || [], loading: false });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik', loading: false });
    }
  },

  fetchPlan: async (planId) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.get(`/wedding/plans/${planId}`);
      const { plan, categories, tasks, expenses, guests } = data.data;
      set({
        currentPlan: plan,
        categories: categories || [],
        tasks: (tasks || []).map(t => ({ ...t, is_completed: !!t.is_completed })),
        expenses: expenses || [],
        guests: guests || [],
        loading: false,
      });
      return plan;
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik', loading: false });
      return null;
    }
  },

  createPlan: async (planData) => {
    set({ loading: true, error: null });
    try {
      const { data } = await api.post('/wedding/plans', planData);
      const { plan, categories, tasks, expenses, guests } = data.data;
      set({
        currentPlan: plan,
        categories: categories || [],
        tasks: (tasks || []).map(t => ({ ...t, is_completed: !!t.is_completed })),
        expenses: expenses || [],
        guests: guests || [],
        loading: false,
      });
      // Plans ro'yxatini yangilash
      get().fetchPlans();
      return plan;
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik', loading: false });
      return null;
    }
  },

  updatePlan: async (updates) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const { data } = await api.put(`/wedding/plans/${plan.id}`, updates);
      set({ currentPlan: data.data });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  deletePlan: async (planId) => {
    try {
      await api.delete(`/wedding/plans/${planId}`);
      set({
        currentPlan: null, categories: [], tasks: [],
        expenses: [], guests: [], guestGroups: [], guestStats: {},
      });
      get().fetchPlans();
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  clearCurrentPlan: () => set({
    currentPlan: null, categories: [], tasks: [],
    expenses: [], guests: [], guestGroups: [], guestStats: {},
  }),

  // ═══════════════════════════════════════════════════════════════
  // CATEGORIES
  // ═══════════════════════════════════════════════════════════════

  addCategory: async ({ name, icon, color }) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const sort_order = get().categories.length + 1;
      const { data } = await api.post(`/wedding/plans/${plan.id}/categories`, {
        name, icon: icon || 'AppstoreOutlined', color: color || '#607D8B', sort_order,
      });
      set({ categories: [...get().categories, data.data] });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  deleteCategory: async (catId) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      await api.delete(`/wedding/plans/${plan.id}/categories/${catId}`);
      set({
        categories: get().categories.filter(c => c.id !== catId),
        tasks: get().tasks.filter(t => t.category_id !== catId),
        expenses: get().expenses.filter(e => {
          const task = get().tasks.find(t => t.id === e.task_id);
          return !task || task.category_id !== catId;
        }),
      });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // TASKS
  // ═══════════════════════════════════════════════════════════════

  addTask: async ({ category_id, title, estimated_cost }) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const sort_order = get().tasks.filter(t => t.category_id === category_id).length + 1;
      const { data } = await api.post(`/wedding/plans/${plan.id}/tasks`, {
        category_id, title, estimated_cost: estimated_cost || 0, sort_order,
      });
      set({ tasks: [...get().tasks, { ...data.data, is_completed: !!data.data.is_completed }] });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  updateTask: async (taskId, updates) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const { data } = await api.put(`/wedding/plans/${plan.id}/tasks/${taskId}`, updates);
      set({
        tasks: get().tasks.map(t =>
          t.id === taskId ? { ...data.data, is_completed: !!data.data.is_completed } : t
        ),
      });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  deleteTask: async (taskId) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      await api.delete(`/wedding/plans/${plan.id}/tasks/${taskId}`);
      set({
        tasks: get().tasks.filter(t => t.id !== taskId),
        expenses: get().expenses.filter(e => e.task_id !== taskId),
      });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // EXPENSES
  // ═══════════════════════════════════════════════════════════════

  addExpense: async (expenseData) => {
    const plan = get().currentPlan;
    if (!plan) return null;
    try {
      const { data } = await api.post(`/wedding/plans/${plan.id}/expenses`, expenseData);
      set({ expenses: [data.data, ...get().expenses] });
      return data.data;
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
      return null;
    }
  },

  updateExpense: async (expId, updates) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const { data } = await api.put(`/wedding/plans/${plan.id}/expenses/${expId}`, updates);
      set({ expenses: get().expenses.map(e => e.id === expId ? data.data : e) });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  deleteExpense: async (expId) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      await api.delete(`/wedding/plans/${plan.id}/expenses/${expId}`);
      set({ expenses: get().expenses.filter(e => e.id !== expId) });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // GUESTS
  // ═══════════════════════════════════════════════════════════════

  fetchGuests: async (filters = {}) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.status) params.set('status', filters.status);
      if (filters.side) params.set('side', filters.side);
      if (filters.group_name) params.set('group_name', filters.group_name);

      const { data } = await api.get(`/wedding/plans/${plan.id}/guests?${params}`);
      set({
        guests: data.data || [],
        guestGroups: data.groups || [],
        guestStats: data.stats || {},
      });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  addGuest: async (guestData) => {
    const plan = get().currentPlan;
    if (!plan) return null;
    try {
      const { data } = await api.post(`/wedding/plans/${plan.id}/guests`, guestData);
      set({ guests: [data.data, ...get().guests] });
      return data.data;
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
      return null;
    }
  },

  addGuestsBulk: async (guestsArr) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const { data } = await api.post(`/wedding/plans/${plan.id}/guests/bulk`, { guests: guestsArr });
      set({ guests: data.data || [] });
      return data.inserted;
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
      return 0;
    }
  },

  updateGuest: async (guestId, updates) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      const { data } = await api.put(`/wedding/plans/${plan.id}/guests/${guestId}`, updates);
      set({ guests: get().guests.map(g => g.id === guestId ? data.data : g) });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },

  deleteGuest: async (guestId) => {
    const plan = get().currentPlan;
    if (!plan) return;
    try {
      await api.delete(`/wedding/plans/${plan.id}/guests/${guestId}`);
      set({ guests: get().guests.filter(g => g.id !== guestId) });
    } catch (e) {
      set({ error: e.response?.data?.message || 'Xatolik' });
    }
  },
}));
